import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ThemingProps,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Children, cloneElement, useRef, useState } from 'react';

export function ConfirmDialog({
  children,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  colorScheme = 'brand',
  variant = 'primary',
  description = `Are you sure? You can't undo this action afterwards.`,
  title = 'Confirm',
  isDisabled = false,
}: {
  children: JSX.Element;
  onConfirm: () => Promise<void> | void;
  onCancel?: () => Promise<void> | void;
  confirmText?: string;
  cancelText?: string;
  colorScheme?: ThemingProps['colorScheme'];
  description?: string | JSX.Element;
  title?: string;
  variant?: string;
  isDisabled?: boolean;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const onClick = async () => {
    try {
      setIsLoading(true);
      await onConfirm();
    } catch (err) {
      toast({ title: err.message, status: 'error' });
    }
    setIsLoading(false);
    onClose();
  };

  return (
    <>
      {cloneElement(Children.only(children), {
        onClick: isDisabled
          ? undefined
          : (e: MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onOpen();
            },
      })}
      <AlertDialog
        id="confirm_dialog"
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay />
        <AlertDialogContent id="confirm_dialog_content" bg="gray.800" borderRadius="md">
          <AlertDialogHeader id="confirm_dialog_header" fontSize="lg" fontWeight="bold">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody id="confirm_dialog_body">{description}</AlertDialogBody>

          <AlertDialogFooter id="confirm_dialog_footer">
            <Button
              id="confirm_dialog_cancel_button"
              px="8"
              variant="ghost"
              ref={cancelRef}
              onClick={
                onCancel
                  ? () => {
                      onCancel();
                      onClose();
                    }
                  : onClose
              }
            >
              {cancelText}
            </Button>
            <Button
              id="confirm_dialog_confirm_button"
              px="8"
              isLoading={isLoading}
              colorScheme={colorScheme}
              variant={variant}
              onClick={onClick}
              ml={3}
            >
              {confirmText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
